import {
  Button,
  Card,
  Grid,
  TextField,
  Typography,
  CircularProgress,
  Tabs,
  Tab,
  Box,
  Switch,
  FormControlLabel,
  Stack,
  Paper,
  alpha,
  useTheme,
} from "@mui/material";
import {
  Settings as SettingsIcon,
  Gavel as GavelIcon,
  Build as BuildIcon,
  AttachMoney as AttachMoneyIcon,
  Warning as WarningIcon,
  Message as MessageIcon,
  TrendingDown as TrendingDownIcon,
  LocalShipping as LocalShippingIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useFormik, Form, FormikProvider } from "formik";
import { apiAuth } from "../../services/AllServices";
import errorHandler from "../../utils/errorHandler";
import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import * as Yup from "yup";
import "../../styles/styles.css";
import SelectField from "../../components/Inputs/SelectField";
import { trimFormValues } from "../../utils/helpers";

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box className="global-settings-tab-panel">{children}</Box>}
    </div>
  );
}

function GlobalSetting() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tabIndex, setTabIndex] = useState(0);

  const settingData = async () => {
    try {
      setLoading(true);
      const response = await apiAuth.getSetting();
      setData(response.data.data);
    } catch (error) {
      errorHandler(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    settingData();
  }, []);

  const initialValues = {
    id: data?._id || "",  
    priceDropInterval: data?.priceDropInterval ?? "",
    priceDropValue: data?.priceDropValue ?? "",
    maintenanceMode: data?.maintenanceMode || false,
    // New Fields
    minimumBidIncrement: data?.minimumBidIncrement ?? "",
    defaultPriceDropIntervalUnit: data?.defaultPriceDropIntervalUnit || "seconds",
    defaultPriceDropIntervalValue: data?.defaultPriceDropIntervalValue ?? "",
    defaultPriceDropValue: data?.defaultPriceDropValue ?? "",
    maintenanceMessage: data?.maintenanceMessage || "",
    claimPointCrossoverMessage: data?.claimPointCrossoverMessage || "",
    dealerBidWarningThreshold: data?.dealerBidWarningThreshold ?? "",
    dealerBidWarningMessage: data?.dealerBidWarningMessage || "",
    shippingCharge: data?.shippingCharge ?? "",
  };

  // Helper function to validate number format (max 8 digits, 2 decimal places)
  const validateNumberFormat = (value) => {
    if (value === null || value === undefined || value === '') return true; // Let required handle empty
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) return false;
    const str = String(numValue);
    const parts = str.split('.');
    const integerPart = parts[0].replace(/^-/, ''); // Remove negative sign if present
    // Check max 8 digits total (including decimal part)
    const totalDigits = integerPart.length + (parts[1] ? parts[1].length : 0);
    if (totalDigits > 8) return false;
    // Check max 2 decimal places
    if (parts[1] && parts[1].length > 2) return false;
    return true;
  };

  const validationSchema = Yup.object().shape({
    // New Fields validations
    minimumBidIncrement: Yup.number()
      .typeError("Minimum bid increment must be a number")
      .min(1, "Must be at least 1")
      .required("Minimum bid increment is required")
      .test('number-format', 'Maximum 8 digits and 2 decimal places allowed', validateNumberFormat),
    defaultPriceDropIntervalUnit: Yup.string()
      .trim()
      .required("Price drop interval unit is required")
      .test('not-only-spaces', "Price drop interval unit is required", (value) => {
        return value && value.trim().length > 0;
      }),
    defaultPriceDropIntervalValue: Yup.number()
      .typeError("Price drop interval value must be a number")
      .min(1, "Must be at least 1")
      .required("Price drop interval value is required")
      .test('number-format', 'Maximum 8 digits and 2 decimal places allowed', validateNumberFormat),
    defaultPriceDropValue: Yup.number()
      .typeError("Price drop value must be a number")
      .min(1, "Must be at least 1")
      .required("Price drop value is required")
      .test('number-format', 'Maximum 8 digits and 2 decimal places allowed', validateNumberFormat),
    maintenanceMessage: Yup.string()
      .trim()
      .when('maintenanceMode', {
        is: true,
        then: (schema) => schema
          .required("Maintenance message is required")
          .test('not-only-spaces', "Maintenance message is required", (value) => {
            return value && value.trim().length > 0;
          })
          .test('no-leading-trailing-spaces', "Message cannot start or end with spaces", (value) => {
            if (!value) return true;
            return value === value.trim();
          }),
        otherwise: (schema) => schema
          .test('no-leading-trailing-spaces', "Message cannot start or end with spaces", (value) => {
            if (!value) return true;
            return value === value.trim();
          }),
      }),
    claimPointCrossoverMessage: Yup.string()
      .trim()
      .required("Claim point crossover message is required")
      .test('not-only-spaces', "Claim point crossover message is required", (value) => {
        return value && value.trim().length > 0;
      })
      .test('no-leading-trailing-spaces', "Message cannot start or end with spaces", (value) => {
        if (!value) return true;
        return value === value.trim();
      }),
    dealerBidWarningThreshold: Yup.number()
      .typeError("Dealer bid warning threshold must be a number")
      .min(1, "Must be at least 1")
      .required("Dealer bid warning threshold is required")
      .test('number-format', 'Maximum 8 digits and 2 decimal places allowed', validateNumberFormat),
    dealerBidWarningMessage: Yup.string()
      .trim()
      .required("Dealer bid warning message is required")
      .test('not-only-spaces', "Dealer bid warning message is required", (value) => {
        return value && value.trim().length > 0;
      })
      .test('no-leading-trailing-spaces', "Message cannot start or end with spaces", (value) => {
        if (!value) return true;
        return value === value.trim();
      }),
    shippingCharge: Yup.number()
      .typeError("Shipping charge must be a number")
      .min(0, "Must be at least 0")
      .required("Shipping charge is required")
      .test('number-format', 'Maximum 8 digits and 2 decimal places allowed', validateNumberFormat),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        setSubmitting(true);
        
        // Trim all string values in the form values
        const trimmedValues = trimFormValues(values);
        
        const formData = new FormData();
        
        Object.keys(trimmedValues).forEach(key => {
          if (key !== 'platformLogo' && key !== 'favicon') {
            const value = trimmedValues[key];
            if (typeof value === 'boolean') {
              formData.append(key, value);
            } 
            else if (typeof value === 'string') {
              formData.append(key, value);
            }
            else if (value !== null && value !== undefined && value !== '') {
              formData.append(key, value);
            }
          }
        });
        
        for (let pair of formData.entries()) {
          console.log(pair[0] + ': ' + pair[1]);
        }
        
        const response = await apiAuth.updateSetting(formData);
        
        if (response && (response.status === 200 || response.data)) {
          const message = response?.data?.message || 'Settings updated successfully!';
          toast.success(message, { autoClose: 500 });
          await settingData();
        } else {
          toast.error('Unexpected response from server');
        }
      } catch (error) {
        console.error('Settings update error:', error);
        errorHandler(error);
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update settings. Please try again.';
        toast.error(errorMessage, { autoClose: 3000 });
      } finally {
        setSubmitting(false);
      }
    },
  });

  // Custom submit handler to track submit attempts and open error tabs
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setSubmitAttempted(true);
    
    // Validate first to check for errors
    const validationErrors = await formik.validateForm();
    
    if (Object.keys(validationErrors).length > 0) {
      // If there are errors, set them and touched fields
      formik.setErrors(validationErrors);
      formik.setTouched(
        Object.keys(validationErrors).reduce((acc, key) => {
          acc[key] = true;
          return acc;
        }, {})
      );
      // useEffect will handle tab switching
      return;
    }
    
    // If no errors, proceed with submit
    // submitAttempted will be reset in onSubmit on success
    await formik.handleSubmit(e);
  };

  const { errors, touched, isSubmitting, handleSubmit, setFieldValue, values } = formik;
  console.log("errors",errors);
  const theme = useTheme();

  // Handler for message fields - allow normal typing, trim on blur
  const handleMessageChange = (fieldName, e) => {
    // Allow normal typing - spaces in middle are fine
    setFieldValue(fieldName, e.target.value);
  };

  // Track submit attempts to open error tabs only on Update button click
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // Auto-open tab with errors only after submit attempt
  useEffect(() => {
    if (!submitAttempted) return;
    
    // Wait a bit for formik to set errors after validation
    const timeoutId = setTimeout(() => {
      if (!errors || Object.keys(errors).length === 0) {
        setSubmitAttempted(false);
        return;
      }

      // Define fields for each tab
      const tab0Fields = [
        'minimumBidIncrement',
        'claimPointCrossoverMessage',
        'defaultPriceDropIntervalUnit',
        'defaultPriceDropIntervalValue',
        'defaultPriceDropValue',
        'dealerBidWarningThreshold',
        'dealerBidWarningMessage'
      ];
      const tab1Fields = ['maintenanceMode', 'maintenanceMessage'];
      const tab2Fields = ['shippingCharge'];

      // Check which tabs have errors
      const tab0HasErrors = tab0Fields.some(field => errors[field]);
      const tab1HasErrors = tab1Fields.some(field => errors[field]);
      const tab2HasErrors = tab2Fields.some(field => errors[field]);

      // Priority: Tab 0 first, then Tab 1, then Tab 2
      // If tab 0 has errors, open tab 0
      if (tab0HasErrors) {
        setTabIndex(0);
      } 
      // If tab 0 has no errors but tab 1 has errors, open tab 1
      else if (tab1HasErrors) {
        setTabIndex(1);
      }
      // If tab 0 and tab 1 have no errors but tab 2 has errors, open tab 2
      else if (tab2HasErrors) {
        setTabIndex(2);
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [errors, submitAttempted]);

  // Handler for number input with restrictions (max 8 digits, 2 decimal places)
  const handleNumberChange = (fieldName, value) => {
    // Remove any non-numeric characters except decimal point
    let cleanedValue = value.replace(/[^\d.]/g, '');
    
    // Prevent multiple decimal points
    const parts = cleanedValue.split('.');
    if (parts.length > 2) {
      cleanedValue = parts[0] + '.' + parts.slice(1).join('');
    }
    
    // Limit to 2 decimal places
    if (parts.length === 2 && parts[1].length > 2) {
      cleanedValue = parts[0] + '.' + parts[1].substring(0, 2);
    }
    
    // Check total digits (including decimal part)
    const totalDigits = cleanedValue.replace(/\./g, '').length;
    if (totalDigits > 8) {
      // If exceeds 8 digits, truncate
      const integerPart = parts[0];
      const decimalPart = parts[1] || '';
      if (integerPart.length >= 8) {
        cleanedValue = integerPart.substring(0, 8);
      } else {
        const remainingDigits = 8 - integerPart.length;
        cleanedValue = integerPart + (decimalPart ? '.' + decimalPart.substring(0, remainingDigits) : '');
      }
    }
    
    // Convert to number if not empty
    if (cleanedValue === '' || cleanedValue === '.') {
      setFieldValue(fieldName, '');
    } else {
      const numValue = parseFloat(cleanedValue);
      if (!isNaN(numValue)) {
        setFieldValue(fieldName, cleanedValue);
      }
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
        <FormikProvider value={formik}>
        {/* Header Section */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 4,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: "white",
            borderRadius: 3,
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.common.white, 0.2),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <SettingsIcon sx={{ fontSize: 32 }} />
            </Box>
            <Box>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
            Global Settings
          </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Manage your auction system configuration and preferences
              </Typography>
            </Box>
          </Stack>
        </Paper>

          {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
            <CircularProgress size={60} />
          </Box>
          ) : (
            <Form onSubmit={handleFormSubmit}>
            <Paper elevation={0} sx={{ borderRadius: 3, overflow: "hidden", border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
              <Tabs
                value={tabIndex}
                onChange={(e, newIndex) => setTabIndex(newIndex)}
                sx={{
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  "& .MuiTab-root": {
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "1rem",
                    minHeight: 64,
                    "&.Mui-selected": {
                      color: theme.palette.primary.main,
                    },
                  },
                  "& .MuiTabs-indicator": {
                    height: 3,
                    borderRadius: "3px 3px 0 0",
                  },
                }}
                variant="scrollable"
                scrollButtons="auto"
              >
                <Tab 
                  icon={<GavelIcon />} 
                  iconPosition="start"
                  label="Auction Rules" 
                  sx={{ gap: 1 }}
                />
                <Tab 
                  icon={<BuildIcon />} 
                  iconPosition="start"
                  label="Maintenance Mode" 
                  sx={{ gap: 1 }}
                />
                <Tab 
                  icon={<LocalShippingIcon />} 
                  iconPosition="start"
                  label="Shipping Charge" 
                  sx={{ gap: 1 }}
                />
              </Tabs>

              {/* Tab 1: Auction Rules */}
              <TabPanel value={tabIndex} index={0}>
                <Box sx={{ p: 3 }}>
                <Grid container spacing={3}>
                  {/* Minimum Bid Increment Card */}
                    <Grid item xs={12} md={6}>
                    <Card 
                        elevation={0}
                        sx={{
                          height: "100%",
                          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                          borderRadius: 3,
                          overflow: "hidden",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            boxShadow: theme.shadows[4],
                            transform: "translateY(-2px)",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            p: 2.5,
                            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.dark, 0.05)} 100%)`,
                            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                          }}
                        >
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Box
                              sx={{
                                p: 1,
                                borderRadius: 1.5,
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <AttachMoneyIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                            </Box>
                            <Typography variant="h6" fontWeight={600} color="text.primary">
                          Minimum Bid Increment
                        </Typography>
                          </Stack>
                      </Box>
                        <Box sx={{ p: 3 }}>
                        <TextField
                          name="minimumBidIncrement"
                          label="Minimum Bid Increment"
                          value={values.minimumBidIncrement || ''}
                          onChange={(e) => handleNumberChange("minimumBidIncrement", e.target.value)}
                          onBlur={formik.handleBlur}
                          fullWidth
                          required
                          error={Boolean(touched.minimumBidIncrement && errors.minimumBidIncrement)}
                          helperText={touched.minimumBidIncrement && errors.minimumBidIncrement}
                          inputProps={{ 
                            inputMode: "decimal",
                            pattern: "[0-9]*\\.?[0-9]{0,2}",
                            onWheel: (e) => e.target.blur()
                          }}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: 2,
                              },
                            }}
                        />
                      </Box>
                    </Card>
                  </Grid>

                   {/* Claim Point Crossover Message Card */}
                    <Grid item xs={12}>
                    <Card 
                        elevation={0}
                        sx={{
                          height: "100%",
                          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                          borderRadius: 3,
                          overflow: "hidden",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            boxShadow: theme.shadows[4],
                            transform: "translateY(-2px)",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            p: 2.5,
                            background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.1)} 0%, ${alpha(theme.palette.warning.dark, 0.05)} 100%)`,
                            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                          }}
                        >
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Box
                              sx={{
                                p: 1,
                                borderRadius: 1.5,
                                bgcolor: alpha(theme.palette.warning.main, 0.1),
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <MessageIcon sx={{ fontSize: 24, color: theme.palette.warning.main }} />
                            </Box>
                            <Typography variant="h6" fontWeight={600} color="text.primary">
                          Claim Point Crossover Message
                        </Typography>
                          </Stack>
                      </Box>
                        <Box sx={{ p: 3 }}>
                        <TextField
                          name="claimPointCrossoverMessage"
                          label="Message"
                          value={values.claimPointCrossoverMessage || ''}
                          onChange={(e) => handleMessageChange("claimPointCrossoverMessage", e)}
                          onBlur={(e) => {
                            formik.handleBlur(e);
                            // Trim leading and trailing spaces on blur
                            const trimmed = (e.target.value || '').trim();
                            if (trimmed !== e.target.value) {
                              setFieldValue("claimPointCrossoverMessage", trimmed);
                            }
                          }}
                          fullWidth
                          required
                          multiline
                          rows={3}
                          error={Boolean(touched.claimPointCrossoverMessage && errors.claimPointCrossoverMessage)}
                          helperText={touched.claimPointCrossoverMessage && errors.claimPointCrossoverMessage}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: 2,
                              },
                            }}
                        />
                      </Box>
                    </Card>
                  </Grid>

                  {/* Default Price Drop Settings Card */}
                    <Grid item xs={12} md={6}>
                    <Card 
                        elevation={0}
                        sx={{
                          height: "100%",
                          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                          borderRadius: 3,
                          overflow: "hidden",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            boxShadow: theme.shadows[4],
                            transform: "translateY(-2px)",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            p: 2.5,
                            background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)} 0%, ${alpha(theme.palette.info.dark, 0.05)} 100%)`,
                            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                          }}
                        >
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Box
                              sx={{
                                p: 1,
                                borderRadius: 1.5,
                                bgcolor: alpha(theme.palette.info.main, 0.1),
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <TrendingDownIcon sx={{ fontSize: 24, color: theme.palette.info.main }} />
                            </Box>
                            <Typography variant="h6" fontWeight={600} color="text.primary">
                          Default Price Drop Settings
                        </Typography>
                          </Stack>
                      </Box>
                        <Box sx={{ p: 3 }}>
                          <Stack spacing={3}>
                            <TextField
                              name="defaultPriceDropIntervalValue"
                              label="Price Drop Interval Value"
                              value={values.defaultPriceDropIntervalValue || ''}
                              onChange={(e) => handleNumberChange("defaultPriceDropIntervalValue", e.target.value)}
                              onBlur={formik.handleBlur}
                              fullWidth
                              required
                              error={Boolean(touched.defaultPriceDropIntervalValue && errors.defaultPriceDropIntervalValue)}
                              helperText={touched.defaultPriceDropIntervalValue && errors.defaultPriceDropIntervalValue}
                              inputProps={{ 
                                inputMode: "decimal",
                                pattern: "[0-9]*\\.?[0-9]{0,2}",
                                onWheel: (e) => e.target.blur()
                              }}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: 2,
                                },
                              }}
                            />
                            <SelectField
                              name="defaultPriceDropIntervalUnit"
                              label="Price Drop Interval Unit"
                              options={[
                                { value: "seconds", label: "Seconds" },
                                { value: "minutes", label: "Minutes" },
                                { value: "hours", label: "Hours" },
                                { value: "days", label: "Days" },
                              ]}
                            />
                            <TextField
                              name="defaultPriceDropValue"
                              label="Price Drop Value"
                              value={values.defaultPriceDropValue || ''}
                              onChange={(e) => handleNumberChange("defaultPriceDropValue", e.target.value)}
                              onBlur={formik.handleBlur}
                              fullWidth
                              required
                              error={Boolean(touched.defaultPriceDropValue && errors.defaultPriceDropValue)}
                              helperText={touched.defaultPriceDropValue && errors.defaultPriceDropValue}
                              inputProps={{ 
                                inputMode: "decimal",
                                pattern: "[0-9]*\\.?[0-9]{0,2}",
                                onWheel: (e) => e.target.blur()
                              }}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: 2,
                                },
                              }}
                            />
                          </Stack>
                      </Box>
                    </Card>
                  </Grid>

                  {/* Dealer Bid Warning Settings Card */}
                    <Grid item xs={12}>
                    <Card 
                        elevation={0}
                        sx={{
                          height: "100%",
                          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                          borderRadius: 3,
                          overflow: "hidden",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            boxShadow: theme.shadows[4],
                            transform: "translateY(-2px)",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            p: 2.5,
                            background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.1)} 0%, ${alpha(theme.palette.error.dark, 0.05)} 100%)`,
                            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                          }}
                        >
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Box
                              sx={{
                                p: 1,
                                borderRadius: 1.5,
                                bgcolor: alpha(theme.palette.error.main, 0.1),
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <WarningIcon sx={{ fontSize: 24, color: theme.palette.error.main }} />
                            </Box>
                            <Typography variant="h6" fontWeight={600} color="text.primary">
                          Dealer Bid Warning Settings
                        </Typography>
                          </Stack>
                      </Box>
                        <Box sx={{ p: 3 }}>
                          <Stack spacing={3}>
                            <TextField
                              name="dealerBidWarningThreshold"
                              label="Threshold (%)"
                              value={values.dealerBidWarningThreshold || ''}
                              onChange={(e) => handleNumberChange("dealerBidWarningThreshold", e.target.value)}
                              onBlur={formik.handleBlur}
                              fullWidth
                              required
                              error={Boolean(touched.dealerBidWarningThreshold && errors.dealerBidWarningThreshold)}
                              helperText={touched.dealerBidWarningThreshold && errors.dealerBidWarningThreshold}
                              inputProps={{ 
                                inputMode: "decimal",
                                pattern: "[0-9]*\\.?[0-9]{0,2}",
                                onWheel: (e) => e.target.blur()
                              }}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: 2,
                                },
                              }}
                            />
                            <TextField
                              name="dealerBidWarningMessage"
                              label="Message"
                              value={values.dealerBidWarningMessage || ''}
                              onChange={(e) => handleMessageChange("dealerBidWarningMessage", e)}
                              onBlur={(e) => {
                                formik.handleBlur(e);
                                // Trim leading and trailing spaces on blur
                                const trimmed = (e.target.value || '').trim();
                                if (trimmed !== e.target.value) {
                                  setFieldValue("dealerBidWarningMessage", trimmed);
                                }
                              }}
                              fullWidth
                              required
                              multiline
                              rows={3}
                              error={Boolean(touched.dealerBidWarningMessage && errors.dealerBidWarningMessage)}
                              helperText={touched.dealerBidWarningMessage && errors.dealerBidWarningMessage}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: 2,
                                },
                              }}
                            />
                          </Stack>
                      </Box>
                    </Card>
                    </Grid>
                  </Grid>
                </Box>
              </TabPanel>

              {/* Tab 2: Maintenance Mode */}
              <TabPanel value={tabIndex} index={1}>
                <Box sx={{ p: 3 }}>
                  <Card
                    elevation={0}
                    sx={{
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      borderRadius: 3,
                      overflow: "hidden",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        boxShadow: theme.shadows[4],
                      },
                    }}
                  >
                    <Box sx={{ p: 3 }}>
                      <Stack spacing={3}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2.5,
                            bgcolor: alpha(theme.palette.warning.main, 0.05),
                            borderRadius: 2,
                            border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                          }}
                        >
                    <FormControlLabel
                      control={
                        <Switch
                          name="maintenanceMode"
                          checked={formik.values.maintenanceMode}
                          onChange={formik.handleChange}
                          color="warning"
                                size="medium"
                              />
                            }
                            label={
                              <Box>
                                <Typography variant="body1" fontWeight={600}>
                                  Enable Maintenance Mode
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  When enabled, the system will be in maintenance mode
                                </Typography>
                              </Box>
                            }
                            sx={{ m: 0 }}
                          />
                        </Paper>
                    <TextField
                      name="maintenanceMessage"
                      label="Maintenance Message"
                      value={values.maintenanceMessage || ''}
                      onChange={(e) => handleMessageChange("maintenanceMessage", e)}
                      onBlur={(e) => {
                        formik.handleBlur(e);
                        // Trim leading and trailing spaces on blur
                        const trimmed = (e.target.value || '').trim();
                        if (trimmed !== e.target.value) {
                          setFieldValue("maintenanceMessage", trimmed);
                        }
                      }}
                      fullWidth
                      required={formik.values.maintenanceMode}
                      multiline
                      rows={4}
                      error={Boolean(touched.maintenanceMessage && errors.maintenanceMessage)}
                      helperText={touched.maintenanceMessage && errors.maintenanceMessage}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                            },
                          }}
                        />
                      </Stack>
                    </Box>
                  </Card>
                </Box>
              </TabPanel>

              {/* Tab 3: Shipping Charge */}
              <TabPanel value={tabIndex} index={2}>
                <Box sx={{ p: 3 }}>
                  <Card
                    elevation={0}
                    sx={{
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      borderRadius: 3,
                      overflow: "hidden",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        boxShadow: theme.shadows[4],
                        transform: "translateY(-2px)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        p: 2.5,
                        background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.dark, 0.05)} 100%)`,
                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box
                          sx={{
                            p: 1,
                            borderRadius: 1.5,
                            bgcolor: alpha(theme.palette.success.main, 0.1),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <LocalShippingIcon sx={{ fontSize: 24, color: theme.palette.success.main }} />
                        </Box>
                        <Typography variant="h6" fontWeight={600} color="text.primary">
                          Shipping Charge
                        </Typography>
                      </Stack>
                    </Box>
                    <Box sx={{ p: 3 }}>
                      <TextField
                        name="shippingCharge"
                        label="Shipping Charge"
                        value={values.shippingCharge || ''}
                        onChange={(e) => handleNumberChange("shippingCharge", e.target.value)}
                        onBlur={formik.handleBlur}
                        fullWidth
                        required
                        error={Boolean(touched.shippingCharge && errors.shippingCharge)}
                        helperText={touched.shippingCharge && errors.shippingCharge}
                        inputProps={{ 
                          inputMode: "decimal",
                          pattern: "[0-9]*\\.?[0-9]{0,2}",
                          onWheel: (e) => e.target.blur()
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                          },
                        }}
                      />
                    </Box>
                  </Card>
                </Box>
              </TabPanel>

              {/* Action Buttons */}
              <Box
                sx={{
                  p: 3,
                  mt: 2,
                  bgcolor: alpha(theme.palette.grey[100], 0.5),
                  borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                }}
              >
                <Stack direction="row" spacing={2} justifyContent="flex-end" flexWrap="wrap">
                    <Button
                      size="large"
                      variant="outlined"
                      onClick={() => {
                        if (!isSubmitting) navigate("/dashboard");
                      }}
                    sx={{
                      minWidth: 140,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                      px: 3,
                      py: 1.5,
                      borderColor: alpha(theme.palette.text.primary, 0.3),
                      color: theme.palette.text.primary,
                      "&:hover": {
                        borderColor: theme.palette.text.primary,
                        bgcolor: alpha(theme.palette.grey[100], 0.5),
                      },
                    }}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="large"
                      type="submit"
                      variant="contained"
                      disabled={isSubmitting}
                    sx={{
                      minWidth: 180,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                      px: 4,
                      py: 1.5,
                      background: "#e96c0e",
                      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                      "&:hover": {
                        background: "#f1a56bff",
                        boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`,
                        transform: "translateY(-2px)",
                      },
                      "&:disabled": {
                        background: theme.palette.grey[400],
                        boxShadow: "none",
                      },
                      transition: "all 0.3s ease",
                    }}
                    >
                      {isSubmitting ? (
                      <Stack direction="row" spacing={1.5} alignItems="center">
                          <CircularProgress size={20} color="inherit" />
                        <Typography>Updating Settings...</Typography>
                      </Stack>
                      ) : (
                      "Update Settings"
                      )}
                    </Button>
                </Stack>
              </Box>
            </Paper>
            </Form>
          )}
        </FormikProvider>
    </Box>
  );
}

export default GlobalSetting;
